/**
 * Textos da Conta (Preferências e Computadores) e das telas do Watchdog (decisões 187 a 195).
 * O português fica como padrão no código (chaves `acc_*` em src/i18n/locales); en, es e fr vêm
 * dos arquivos de idioma. `{{theProduct}}` leva o artigo certo em português, como em screenText.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '../../hooks/useBrand';
import { localeOf } from '../home/homeText';
import type { WatchdogDetails, WatchdogDevice, WatchdogRequest } from '../../api/watchdog';
import { numericDateLocale } from '../../lib/format';

export const ACCOUNT_PT = {
  bar_pending_one: 'Conta, {{n}} pedido de acesso',
  bar_pending_other: 'Conta, {{n}} pedidos de acesso',
  menu_st_active: 'Licença ativa até {{date}}',
  menu_st_ending_one: 'Licença vence em {{n}} dia',
  menu_st_ending_other: 'Licença vence em {{n}} dias',
  menu_st_today: 'Licença vence hoje',
  menu_st_expired: 'Licença encerrada em {{date}}',
  menu_st_lifetime: 'Licença vitalícia',
  menu_st_subscription: 'Assinatura ativa',
  menu_st_trial: 'Você está no teste',
  menu_renew: 'Renovar',
  menu_plans: 'Ver os planos',
  menu_license: 'Licença',
  menu_license_all: 'Situação, computadores e pagamentos',
  menu_license_no_pc: 'Situação e pagamentos',
  menu_license_no_pay: 'Situação e computadores',
  menu_license_only: 'Situação e renovação',
  menu_profile: 'Perfil e preferências',
  menu_profile_sub: 'Seu negócio e idioma do painel',
  menu_pending_one: '{{n}} pedido',
  menu_pending_other: '{{n}} pedidos',
  new_tab: '(abre em outra aba)',
  lic_title: 'Licença',
  lic_situation: 'Situação',
  lic_active: 'Ativa até {{date}}',
  lic_ending_one: 'Vence em {{n}} dia',
  lic_ending_other: 'Vence em {{n}} dias',
  lic_today: 'Vence hoje',
  lic_expired: 'Encerrada em {{date}}',
  lic_lifetime: 'Vitalícia',
  lic_subscription: 'Assinatura ativa',
  lic_trial: 'Você está no teste',
  lic_left_one: 'Falta {{n}} dia',
  lic_left_other: 'Faltam {{n}} dias',
  lic_plan: 'Último plano: {{plan}}',
  lic_expired_text: 'Para voltar a capturar, renove a licença.',
  lic_lifetime_text: 'Sua licença não vence.',
  lic_subscription_text: 'Para mudar ou cancelar a assinatura, fale com a gente.',
  lic_trial_text:
    'Os 5 primeiros resultados de cada lista ficam legíveis. Com um plano, você vê e baixa todos.',
  lic_renew: 'Renovar',
  lic_renew_hint: 'Abre a página de compra em outra aba',
  lic_plans: 'Ver os planos',
  lic_code: 'Tenho um código de ativação',
  lic_talk: 'Fale conosco',
  pc_title: 'Computadores',
  pc_count_one: '{{n}} computador',
  pc_count_other: '{{n}} computadores',
  pc_rule_title: 'Sua licença é individual',
  pc_rule:
    'Ela vale só para você e não pode ser compartilhada nem repassada. Você pode usá-la nos seus computadores, um de cada vez. Trocar de computador mais de uma vez no mesmo dia tem espera.',
  name_on: '{{browser}} no {{os}}',
  pc_this: 'Este computador',
  pc_in_use: 'Em uso agora',
  pc_last: 'Último uso: {{when}}',
  pc_yesterday: 'ontem, {{time}}',
  pc_today: 'hoje, {{time}}',
  pc_tomorrow: 'amanhã, {{time}}',
  pc_date_time: '{{date}}, {{time}}',
  pc_never: 'Ainda sem uso',
  pc_screen: 'tela {{width}} × {{height}}',
  pc_authorized: 'autorizado em {{date}}',
  pc_remove: 'Remover',
  pc_remove_aria: 'Remover {{name}}',
  pc_new:
    'Para usar em outro computador, abra o painel nele e peça acesso. Você autoriza por aqui ou pelo link que chega em {{email}}.',
  req_title: '{{name}} pediu para usar a sua licença',
  req_when: 'Pedido às {{time}} · vale até {{until}}',
  req_refuse: 'Recusar',
  req_approve: 'Autorizar',
  toast_approved: '{{name}} autorizado.',
  toast_expired: 'O pedido de {{name}} venceu.',
  toast_refused: 'Pedido recusado.',
  toast_removed: 'Computador removido.',
  toast_error: 'Não deu certo agora. Tente de novo.',
  rm_title: 'Remover {{name}}?',
  rm_text:
    'Ele deixa de usar a sua licença. Para voltar a usar nele, será preciso autorizar de novo.',
  rm_cancel: 'Cancelar',
  rm_ok: 'Remover computador',
  rm_busy: 'Removendo…',
  rm_error: 'Não deu certo agora. Tente de novo.',
  ren_title: 'Renovação automática',
  ren_lead: 'O que renova sozinho, no mesmo valor e no mesmo período.',
  ren_line: '{{amount}} {{cycle}}, {{method}}',
  ren_cycle_1: 'todo mês',
  ren_cycle_3: 'a cada 3 meses',
  ren_cycle_6: 'a cada 6 meses',
  ren_cycle_12: 'todo ano',
  ren_card: 'no cartão',
  ren_pix: 'por Pix Automático',
  ren_active: 'Ativa',
  ren_past_due:
    'A última cobrança não foi paga. Confira o cartão ou o Pix Automático no seu banco.',
  ren_last: 'Última cobrança em {{date}}',
  ren_canceled: 'Cancelada',
  ren_canceled_at: 'Cancelada em {{date}}. O período já pago continua valendo.',
  ren_cancel: 'Cancelar renovação',
  ren_cancel_aria: 'Cancelar a renovação de {{what}}',
  ren_note:
    'Cancelar para as próximas cobranças. Você continua usando até o fim do período já pago.',
  ren_dialog_title: 'Cancelar a renovação automática?',
  ren_dialog_text:
    'Você não será mais cobrado por {{what}}. Continua usando até o fim do período já pago e pode renovar quando quiser.',
  ren_keep: 'Manter renovação',
  ren_ok: 'Cancelar renovação',
  ren_busy: 'Cancelando…',
  ren_error: 'Não deu certo agora. Tente de novo.',
  ren_toast: 'Renovação automática cancelada.',
  up_title: 'Subir para o Avançado',
  up_line: 'Por {{amount}} agora, e a licença continua até {{date}}',
  up_lead_one: 'Você paga só pelo dia que falta da licença.',
  up_lead_other: 'Você paga só pelos {{n}} dias que faltam da licença.',
  up_button: 'Subir para o Avançado',
  up_auto_renew: 'Com a renovação automática ligada, a troca para o Avançado é feita pelo suporte.',
  up_close: 'Fechar',
  up_back: 'Voltar',
  up_rule: 'Você paga a diferença entre os planos só pelo tempo que falta. Na metade da licença, paga a metade.',
  up_why: 'Como o valor é calculado',
  up_months_1: 'mensal',
  up_months_3: 'trimestral',
  up_months_6: 'semestral',
  up_months_12: 'anual',
  up_row_to: 'Avançado, {{period}}',
  up_row_from: 'Seu plano, {{period}}',
  up_row_diff: 'Diferença',
  up_row_days: 'Faltam {{n}} dos {{total}} dias',
  up_row_total: 'Você paga agora',
  up_note: 'A data de fim não muda: a licença vale até {{date}}, já no Avançado.',
  up_tax_label: 'CPF ou CNPJ',
  up_tax_text: 'Para emitir a nota fiscal do pagamento.',
  up_tax_invalid: 'Confira o número: o CPF tem 11 dígitos e o CNPJ, 14.',
  up_failed: 'Não deu certo agora. Tente de novo.',
  up_refused: 'O pagamento não foi aceito. Tente de novo ou use outra forma.',
  up_expired: 'O prazo deste pagamento acabou. Gere outro.',
  up_canceled: 'O pagamento não foi concluído. Você pode tentar de novo.',
  up_pending:
    'Há um pagamento do upgrade em andamento. Aguarde a confirmação; se ele não vier, tente de novo em alguns minutos.',
  up_pay_pix: 'Pagar com Pix',
  up_pay_card: 'Pagar com cartão',
  up_busy: 'Gerando o pagamento…',
  up_pix_text: 'Abra o app do seu banco, escolha pagar com Pix e leia o QR Code ou cole o código. Valor: {{amount}}.',
  up_pix_qr: 'QR Code do Pix',
  up_pix_copy: 'Copiar código Pix',
  up_pix_copied: 'Código copiado',
  up_pix_until: 'Vale até {{time}}.',
  up_waiting: 'Assim que o pagamento for confirmado, esta janela avisa.',
  up_confirming: 'Confirmando o pagamento…',
  up_card_text: 'O pagamento com cartão abre numa página segura, em outra aba. Depois de pagar, volte aqui: esta janela avisa quando confirmar.',
  up_card_open: 'Abrir a página de pagamento',
  up_done_title: 'Pronto, você está no Avançado',
  up_done_text: 'Os recursos do Avançado já valem, até {{date}}.',
  up_done_short: 'Os recursos do Avançado já valem.',
  up_done_ok: 'Continuar',
  pay_title: 'Pagamentos',
  pay_lead: 'Os pagamentos feitos com o email {{email}}.',
  pay_col_date: 'Data',
  pay_col_item: 'Compra',
  pay_col_method: 'Forma',
  pay_col_amount: 'Valor',
  pay_col_invoice: 'Nota fiscal',
  pay_bonus_one: '+ {{n}} bônus',
  pay_bonus_other: '+ {{n}} bônus',
  pay_period: 'Período: {{period}}',
  pay_refunded: 'Reembolsado',
  pay_pix: 'Pix',
  pay_card: 'Cartão',
  pay_card_n: 'Cartão em {{n}}x',
  pay_boleto: 'Boleto',
  inv_pending: 'Em emissão',
  inv_canceled: 'Cancelada',
  inv_ask: 'Pedir a nota',
  inv_label: 'Nota de {{date}} em {{format}} {{tab}}',
  pay_all: 'Ver todos os {{n}} pagamentos',
  pay_less: 'Mostrar só os mais recentes',
  pay_error: 'Não conseguimos carregar seus pagamentos agora.',
  retry: 'Tentar de novo',
  pay_loading: 'Carregando seus pagamentos…',
  prof_page_title: 'Perfil e preferências',
  prof_title: 'Seu negócio',
  prof_lead: 'Usamos estas respostas para sugerir buscas e territórios.',
  prof_edit: 'Editar',
  prof_complete: 'Completar',
  prof_sector: 'Setor',
  prof_segment: 'Segmento',
  prof_customer: 'Para quem você vende',
  prof_reach: 'Onde estão seus clientes',
  prof_none: 'Não informado',
  prof_segment_other_label: 'Qual segmento?',
  prof_segment_other_ph: 'Ex.: Estúdio de pilates',
  prof_sector_pick: 'Escolha o setor',
  prof_segment_pick: 'Escolha o segmento',
  prof_segment_first: 'Escolha antes o setor',
  prof_cancel: 'Cancelar',
  prof_save: 'Salvar',
  prof_saving: 'Salvando…',
  prof_saved: 'Perfil salvo.',
  prof_error: 'Não conseguimos salvar agora. Tente de novo.',
  prefs_title: 'Preferências',
  pref_language: 'Idioma do painel',
  pref_language_hint: 'Muda na hora e vale também nos seus outros computadores e nos emails.',
  pref_error: 'Não conseguimos salvar o idioma agora. Tente de novo.',
  pref_changed: 'Painel em {{language}}.',
  pref_tab_groups: 'Agrupar as abas da busca',
  pref_tab_groups_hint:
    'As abas que abrem na captura ficam juntas num grupo amarelo, separadas das suas. Ligar pede a sua permissão no Chrome.',
  notice_title: 'Novo computador pedindo acesso',
  notice_text: '{{name}} pediu para usar a sua licença.',
  notice_later: 'Ver depois',
  auth_title: 'Autorize este computador',
  auth_text:
    'Sua licença ainda não está neste computador. Para usar aqui, autorize de um destes jeitos:',
  auth_by_email: 'Pelo link que enviamos para {{email}}.',
  auth_by_device: 'No computador que está com a licença, em Conta › Licença.',
  auth_waiting: 'Esperando a autorização · o pedido vale até {{until}}',
  auth_resend: 'Reenviar o email',
  auth_resent: 'Email reenviado.',
  refused_title: 'O pedido foi recusado',
  refused_text: 'O acesso deste computador foi recusado no computador que está com a licença.',
  expired_title: 'O pedido venceu',
  expired_text: 'Ninguém autorizou este computador até {{until}}.',
  ask_again: 'Pedir de novo',
  paused_title: 'Sua licença está em uso em outro computador',
  paused_text: '{{name}} está usando a licença agora. Ela vale em um computador por vez.',
  use_here: 'Usar neste computador',
  paused_note: '{{name}} fica pausado até voltar a ser usado.',
  paused_wait:
    'A licença já trocou de computador hoje. Você poderá usar aqui a partir de {{when}}, {{hours}} horas depois do último uso em {{name}}.',
  wd_individual:
    'Sua licença é individual: vale só para você, nos seus computadores, um de cada vez.',
  link_individual: 'Autorize só computadores seus. A licença é individual.',
  period_month: '1 mês',
  period_months: '{{n}} meses',
  period_year: '1 ano',
  period_years: '{{n}} anos',
  period_day: '1 dia',
  period_days: '{{n}} dias',
  prof_segment_other: 'Outro',
  cancel: 'Cancelar',
  lang_pt: 'Português (Brasil)',
  lang_en: 'English',
  lang_es: 'Español',
  lang_fr: 'Français',
  dev_error: 'Não deu certo agora. Tente de novo.',
  name_unknown: 'Computador sem nome',
  auth_email_failed:
    'Não conseguimos enviar o email agora. Use o outro computador ou reenvie o email.',
  auth_rate: 'Muitos pedidos seguidos. Espere um pouco e tente de novo.',
  switched_title: 'Você abriu {{theProduct}} em outro computador',
  switched_text:
    'Agora a licença está em <b>{{device}}</b>. Se havia uma captura rodando aqui, ela foi pausada e o que já veio está salvo.',
  link_title: 'Autorizar acesso de outro computador',
  link_text: 'Este computador pediu para usar a sua licença {{ofProduct}}:',
  link_when: 'Pedido em {{date}}, às {{time}}',
  link_approve: 'Autorizar este computador',
  link_refuse: 'Não fui eu, recusar',
  link_approved_title: 'Computador autorizado',
  link_approved_text: 'Pronto. O outro computador já pode usar {{theProduct}}.',
  link_refused_title: 'Pedido recusado',
  link_refused_text: 'Esse computador não vai usar a sua licença.',
  link_closed_title: 'Este pedido não está mais aberto',
  link_closed_text:
    'Ele já foi decidido ou passou do prazo. Se precisar, peça de novo no computador novo.',
  link_invalid_title: 'Link inválido',
  link_invalid_text:
    'Este link de autorização não existe ou foi trocado por um mais novo. Use o último email que enviamos.',
  loading: 'Carregando…',
} as const;

export type AccountKey = keyof typeof ACCOUNT_PT;
type Vars = Record<string, string | number>;

/** Nome do sistema na linha de detalhes: o servidor diz "Mac", a linha diz "macOS 15". */
const OS_LABELS: Record<string, string> = { Mac: 'macOS' };

export function useAccountText() {
  const { t, i18n } = useTranslation();
  const brand = useBrand();
  const lang = (i18n?.language ?? 'pt').slice(0, 2);
  const locale = localeOf(lang);
  const product = brand.appTitle;

  const words = useMemo<Vars>(() => {
    if (lang !== 'pt')
      return { product, theProduct: product, TheProduct: product, ofProduct: product };
    const female = brand.source === 'lead-machine';
    return {
      product,
      theProduct: `${female ? 'a' : 'o'} ${product}`,
      TheProduct: `${female ? 'A' : 'O'} ${product}`,
      ofProduct: `${female ? 'da' : 'do'} ${product}`,
    };
  }, [lang, product, brand.source]);

  const tx = useCallback(
    (key: AccountKey, vars?: Vars): string =>
      t(`acc_${key}`, {
        defaultValue: ACCOUNT_PT[key],
        ...words,
        ...vars,
        interpolation: { escapeValue: false },
      }) as string,
    [t, words],
  );

  const date = useCallback(
    (value: string | Date): string =>
      new Date(value).toLocaleDateString(numericDateLocale(locale), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [locale],
  );
  const time = useCallback(
    (value: string | Date): string =>
      new Date(value).toLocaleTimeString(numericDateLocale(locale), {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale],
  );

  /** "Chrome no Windows". */
  const deviceName = useCallback(
    (item: Pick<WatchdogDevice | WatchdogRequest, 'browser' | 'os'> | null | undefined): string => {
      if (!item) return tx('name_unknown');
      if (item.browser && item.os) return tx('name_on', { browser: item.browser, os: item.os });
      return item.browser || item.os || tx('name_unknown');
    },
    [tx],
  );

  /** Período do código de ativação: "1 mês", "3 meses", "1 ano", "5 anos" (ou dias, se curto). */
  const period = useCallback(
    (days: number): string => {
      if (days < 28) return days === 1 ? tx('period_day') : tx('period_days', { n: days });
      if (days <= 35) return tx('period_month');
      if (days <= 300) return tx('period_months', { n: Math.round(days / 31) });
      if (days <= 400) return tx('period_year');
      return tx('period_years', { n: Math.round(days / 365) });
    },
    [tx],
  );

  /** Singular e plural ("1 dia", "3 dias"); no francês, 0 e 1 ficam no singular. */
  const plural = useCallback(
    (base: string, n: number): string =>
      tx(`${base}_${(lang === 'fr' ? n <= 1 : n === 1) ? 'one' : 'other'}` as AccountKey, { n }),
    [tx, lang],
  );

  /**
   * Dia e hora perto de hoje: "hoje, 14:10", "ontem, 18:40", "amanhã, 06:10" ou
   * "22/10/2026, 18:40". Serve ao último uso e ao fim da espera da troca.
   */
  const when = useCallback(
    (value: string | Date): string => {
      const d = new Date(value);
      const now = new Date();
      const day = (x: Date): number =>
        new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
      const diff = Math.round((day(now) - day(d)) / (24 * 60 * 60 * 1000));
      if (diff === 0) return tx('pc_today', { time: time(d) });
      if (diff === 1) return tx('pc_yesterday', { time: time(d) });
      if (diff === -1) return tx('pc_tomorrow', { time: time(d) });
      return tx('pc_date_time', { date: date(d), time: time(d) });
    },
    [tx, date, time],
  );

  /**
   * "Chrome 141 · macOS 15 · tela 1512 × 982 · autorizado em 24/10/2026" (decisão 247). Sem
   * versões nem tela (computador de antes), só a data da autorização.
   */
  const detailsLine = useCallback(
    (item: WatchdogDetails & { authorizedAt?: string | null }): string => {
      const parts: string[] = [];
      if (item.browserVersion || item.osVersion || item.screen) {
        if (item.browser) {
          parts.push(item.browserVersion ? `${item.browser} ${item.browserVersion}` : item.browser);
        }
        if (item.os) {
          const os = OS_LABELS[item.os] ?? item.os;
          parts.push(item.osVersion ? `${os} ${item.osVersion}` : os);
        }
        if (item.screen) {
          parts.push(tx('pc_screen', { width: item.screen.width, height: item.screen.height }));
        }
      }
      if (item.authorizedAt) parts.push(tx('pc_authorized', { date: date(item.authorizedAt) }));
      return parts.join(' · ');
    },
    [tx, date],
  );

  /** "R$ 797,00". */
  const money = useCallback(
    (value: number, currency: string): string => {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency || 'BRL',
        }).format(value);
      } catch {
        return value.toFixed(2);
      }
    },
    [locale],
  );

  return { tx, plural, date, time, when, deviceName, detailsLine, period, money, lang };
}

/** "pes•••@clinicasorriso.com.br": o email da compra, sem mostrar tudo numa tela sem a vez. */
export function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return email;
  return `${email.slice(0, Math.min(3, at))}•••${email.slice(at)}`;
}

/** Ícone pelo sistema: laptop_mac, laptop_windows ou computer. */
export function deviceIcon(os: string | null | undefined): string {
  const name = (os ?? '').toLowerCase();
  if (name.includes('mac')) return 'laptop_mac';
  if (name.includes('windows')) return 'laptop_windows';
  return 'computer';
}

/** Escapa texto que vem do servidor antes de entrar em <Html>. */
export function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
